import { useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, AlertCircle, CheckCircle, XCircle, Eye } from "lucide-react";
import { toast } from "sonner";

export default function AdminApartments() {
  const { user } = useAuth();
  const [selectedListing, setSelectedListing] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const { data: pendingListings, isLoading, refetch } = trpc.apartments.adminListPending.useQuery({});
  const getListingMutation = trpc.apartments.adminGetListing.useQuery(
    { id: selectedListing?.id || 0 },
    { enabled: selectedListing !== null }
  );
  const approveMutation = trpc.apartments.adminApproveListing.useMutation();
  const rejectMutation = trpc.apartments.adminRejectListing.useMutation();

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
        <div className="container mx-auto px-4">
          <Card className="max-w-md mx-auto p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600">You don't have permission to access this page.</p>
          </Card>
        </div>
      </div>
    );
  }

  const handleApprove = async (listingId: number) => {
    try {
      await approveMutation.mutateAsync({
        id: listingId,
        notes: "Approved by admin",
      });
      toast.success("Listing approved successfully");
      setSelectedListing(null);
      refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to approve listing");
    }
  };

  const handleReject = async (listingId: number) => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    try {
      await rejectMutation.mutateAsync({
        id: listingId,
        notes: rejectionReason,
      });
      toast.success("Listing rejected successfully");
      setSelectedListing(null);
      setRejectionReason("");
      refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to reject listing");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Apartment Listings Approval</h1>
          <p className="text-lg text-slate-600">Review and approve pending apartment listings</p>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin w-8 h-8 text-blue-600" />
          </div>
        ) : pendingListings?.listings && pendingListings.listings.length > 0 ? (
          <div className="space-y-4">
            {pendingListings.listings.map((listing) => (
              <Card key={listing.id} className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900">Flat {listing.flatNumber}</h3>
                    <p className="text-slate-600">
                      {listing.bhk} BHK {listing.apartmentType} • {listing.listingType}
                    </p>
                  </div>
                  <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                    Pending Review
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-slate-600">Price</p>
                    <p className="font-semibold text-slate-900">₹{listing.price.toLocaleString("en-IN")}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Posted By</p>
                    <p className="font-semibold text-slate-900">{listing.residentName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="font-semibold text-slate-900 text-sm">{listing.residentEmail}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Posted On</p>
                    <p className="font-semibold text-slate-900">{new Date(listing.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      onClick={() => setSelectedListing(listing)}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Review Details
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Review Listing - Flat {listing.flatNumber}</DialogTitle>
                    </DialogHeader>
                    {getListingMutation.data && (
                      <div className="space-y-6">
                        {/* Listing Details */}
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-3">Listing Details</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-slate-600">Type</p>
                              <p className="font-medium text-slate-900 capitalize">{getListingMutation.data.listingType}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">Price</p>
                              <p className="font-medium text-slate-900">₹{getListingMutation.data.price.toLocaleString("en-IN")}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">Area</p>
                              <p className="font-medium text-slate-900">{getListingMutation.data.areaInSqFt} sq ft</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">Floor</p>
                              <p className="font-medium text-slate-900">{getListingMutation.data.floor || "Not specified"}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">Facing</p>
                              <p className="font-medium text-slate-900">{getListingMutation.data.facing || "Not specified"}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">Availability</p>
                              <p className="font-medium text-slate-900">{getListingMutation.data.availabilityDate}</p>
                            </div>
                          </div>
                        </div>

                        {/* Description */}
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-2">Description</h3>
                          <p className="text-slate-700 whitespace-pre-wrap">{getListingMutation.data.description}</p>
                        </div>

                        {/* Amenities */}
                        {getListingMutation.data.amenities && getListingMutation.data.amenities.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2">Amenities</h3>
                            <div className="flex flex-wrap gap-2">
                              {getListingMutation.data.amenities.map((amenity: string) => (
                                <span key={amenity} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                                  {amenity}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Resident Info */}
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-3">Resident Information</h3>
                          <div className="space-y-2">
                            <div>
                              <p className="text-sm text-slate-600">Name</p>
                              <p className="font-medium text-slate-900">{getListingMutation.data.residentName}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">Email</p>
                              <p className="font-medium text-slate-900">{getListingMutation.data.residentEmail}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-600">Phone</p>
                              <p className="font-medium text-slate-900">{getListingMutation.data.residentPhone}</p>
                            </div>
                          </div>
                        </div>

                        {/* Inquiries */}
                        {getListingMutation.data.inquiries && getListingMutation.data.inquiries.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-3">
                              Inquiries ({getListingMutation.data.inquiries.length})
                            </h3>
                            <div className="space-y-2">
                              {getListingMutation.data.inquiries.map((inquiry: any) => (
                                <div key={inquiry.id} className="bg-slate-50 p-3 rounded">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-medium text-slate-900">{inquiry.inquirerName}</p>
                                      <p className="text-sm text-slate-600">{inquiry.inquirerEmail}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium">Spam Score: {inquiry.spamScore}</p>
                                      {inquiry.isBlocked && (
                                        <p className="text-xs text-red-600 font-medium">Blocked</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Rejection Reason */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Rejection Reason (if rejecting)</label>
                          <Textarea
                            placeholder="Provide a reason for rejection..."
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            rows={3}
                          />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApprove(listing.id)}
                            disabled={approveMutation.isPending}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            {approveMutation.isPending ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Approving...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Approve
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleReject(listing.id)}
                            disabled={rejectMutation.isPending || !rejectionReason.trim()}
                            variant="destructive"
                            className="flex-1"
                          >
                            {rejectMutation.isPending ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Rejecting...
                              </>
                            ) : (
                              <>
                                <XCircle className="w-4 h-4 mr-2" />
                                Reject
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">All Caught Up!</h3>
            <p className="text-slate-600">There are no pending apartment listings to review.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
